import { cn } from "@omni/utils/styles";
import { useEffect, useRef, useState } from "react";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperCore } from "swiper/types";

import { TransportSlot } from "@/api/hooks/cartService.types";
import { Input } from "@/components/Form/Base/input.component";
import { SwiperNavButton } from "@/components/Product/ImageViewer/partials/SwiperNavButton/swiperNavButton.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { formatISO } from "@/utils/date";

type Props = {
    slots: Array<Array<TransportSlot>>;
    activeSlot?: TransportSlot;
    onClick: (slot: TransportSlot) => void;
};

export const SlotSelect = ({ slots, activeSlot, onClick }: Props) => {
    const [allowPrevious, setAllowPrevious] = useState(false);
    const [allowNext, setAllowNext] = useState(true);
    const [mainSwiper, setMainSwiper] = useState<SwiperCore>();
    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);
    const { t } = useGranite();

    const breakpoints = {
        1024: {
            slidesPerView: 4,
            spaceBetween: 20
        },
        992: {
            slidesPerView: 3,
            spaceBetween: 10
        },
        640: {
            slidesPerView: 2,
            spaceBetween: 10
        },
        320: {
            slidesPerView: 1,
            spaceBetween: 10
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        window.addEventListener("hashchange", updateSwiper, {
            once: true
        });
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        setAllowNext(determineStateAllowNext());
    }, [slots.length]);

    const determineStateAllowNext = () => {
        const sortedKeys: Array<keyof typeof breakpoints> = Object.keys(
            breakpoints
        ).sort((a, b) => parseInt(a, 10) - parseInt(b, 10)) as unknown as Array<
            keyof typeof breakpoints
        >;

        const bp = sortedKeys.find((key) => key < screen.availWidth)!;

        const slidesInView = breakpoints[bp].slidesPerView;

        return slots.length > slidesInView;
    };

    const updateSwiper = () => {
        if (location.hash === "#transport-service" && mainSwiper) {
            mainSwiper.update();
            mainSwiper.updateSize();
        }
    };

    const updateButtons = () => {
        if (mainSwiper) {
            setAllowPrevious(!mainSwiper.isBeginning);
            setAllowNext(!mainSwiper.isEnd);
        }
    };

    return (
        <>
            <p>{t("transportService.modal.description")}</p>
            <div className={cn("tw:relative")}>
                <div className={cn("tw:relative tw:flex tw:items-center")}>
                    <SwiperNavButton
                        innerRef={navigationPrevRef}
                        disabled={!allowPrevious}
                        icon="left"
                        onClick={() => {
                            if (allowPrevious && mainSwiper) {
                                mainSwiper.slidePrev();
                            }
                        }}
                    />
                    <Swiper
                        modules={[Navigation]}
                        navigation={{
                            nextEl: navigationNextRef.current,
                            prevEl: navigationPrevRef.current
                        }}
                        updateOnWindowResize={true}
                        onSlideChange={() => updateButtons()}
                        breakpoints={breakpoints}
                        onSwiper={setMainSwiper}
                    >
                        {slots.map((date, index) => (
                            <SwiperSlide
                                key={index}
                                className={cn(
                                    "tw:flex tw:flex-col tw:text-center"
                                )}
                            >
                                <p className={cn("tw:mb-0 tw:font-bold")}>
                                    {formatISO(date[0].startDate, "dd/MM")}
                                </p>
                                {date.map((slot) => (
                                    <div
                                        className={cn(
                                            "tw:p-2 tw:my-2 tw:mx-0 tw:flex tw:flex-col tw:justify-center tw:border-2 tw:border-success tw:cursor-pointer",
                                            {
                                                "tw:border-0 tw:bg-gray-300 tw:text-white tw:cursor-auto":
                                                    !slot.available
                                            }
                                        )}
                                        key={`${slot.startDate}-${slot.endDate}`}
                                        onClick={() => {
                                            if (slot.available) {
                                                onClick(slot);
                                            }
                                        }}
                                    >
                                        <p className="m-0">
                                            {`${formatISO(
                                                slot.startDate,
                                                "kk'u'mm"
                                            )} - ${formatISO(
                                                slot.endDate,
                                                "kk'u'mm"
                                            )}`}
                                        </p>
                                        <Input
                                            type="radio"
                                            name={`${slot.startDate}-${slot.endDate}`}
                                            className={cn(
                                                "tw:relative tw:m-auto tw:cursor-pointer",
                                                {
                                                    "tw:hidden": !slot.available
                                                }
                                            )}
                                            checked={
                                                activeSlot &&
                                                activeSlot.startDate ===
                                                    slot.startDate
                                            }
                                            onClick={() => {
                                                if (slot.available) {
                                                    onClick(slot);
                                                }
                                            }}
                                        />
                                    </div>
                                ))}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    <SwiperNavButton
                        innerRef={navigationNextRef}
                        disabled={!allowNext}
                        icon="right"
                        onClick={() => {
                            if (allowNext && mainSwiper) {
                                mainSwiper.slideNext();
                            }
                        }}
                    />
                </div>
            </div>
        </>
    );
};
